import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, ArrowUpRight, Megaphone } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError } from "../../lib/api";
import StudioFinancePipeline from "../../components/os/StudioFinancePipeline";
import TrashDialog, { TrashButton } from "../../components/os/TrashDialog";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const STATUS = { active: "bg-violet/10 text-violet", paused: "bg-navy/10 text-navy/60", done: "bg-emerald-100 text-emerald-700" };

export default function TagmaStudio() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", budget: "", currency: "MXN" });
  const [share, setShare] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const tf = useTeamFilter();

  const load = () => api.get("/tagma/overview").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/tagma/campaigns", { ...form, budget: Number(form.budget) || 0 }); toast.success("Campaña creada"); setOpen(false); setForm({ name: "", client: "", budget: "", currency: "MXN" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };
  const doDelete = async (c) => {
    if (!window.confirm(`¿Eliminar la campaña "${c.name}"? Se moverá a la papelera y podrás restaurarla durante 30 días.`)) return;
    try { await api.delete(`/tagma/campaigns/${c.id}`); toast.success("Movida a la papelera (30 días)"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  const k = d?.kpis || {};
  const KPIS = [
    { label: "Campañas", value: k.campaigns ?? 0 },
    { label: "Valor", value: money(k.value) },
    { label: "Gastos", value: money(k.expenses) },
    { label: "Utilidad", value: money(k.profit) },
    { label: "ROI", value: `${k.roi ?? 0}%` },
    { label: "ROAS", value: `${k.roas ?? 0}x` },
  ];

  return (
    <OSLayout title="Tagma Studio">
      <div className="space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline text-pink-600 mb-2">Publicidad · Campañas</div>
            <h1 className="text-3xl font-medium tracking-tight">Tagma Studio</h1>
            <p className="text-navy/50 mt-1 text-sm">Gestiona campañas: cotizaciones, facturas, gastos, pagos a proveedores, métricas y ROI.</p>
          </div>
          <div className="flex items-center gap-3">
            <OwnerFilter tf={tf} />
            <TrashButton testid="tagma-trash-btn" onClick={() => setTrashOpen(true)} />
            <button data-testid="tagma-new" onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /> Nueva campaña
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {KPIS.map((m) => (
            <div key={m.label} className="bg-white border border-navy/10 rounded-lg p-4">
              <div className="text-xl font-medium">{m.value}</div>
              <div className="overline text-navy/40 text-[0.6rem] mt-1">{m.label}</div>
            </div>
          ))}
        </div>

        <StudioFinancePipeline studio="tagma" />

        {!d || d.campaigns.length === 0 ? (
          <div data-testid="tagma-empty" className="text-navy/40 py-20 text-center border border-dashed border-navy/15 rounded-lg">
            <Megaphone className="w-8 h-8 mx-auto mb-3 text-navy/20" /> Sin campañas aún. Crea la primera.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tf.filter(d.campaigns).map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: (i % 3) * 0.06 }} className="relative">
                <div className="absolute top-4 right-4 z-10">
                  <ItemMenu testId={c.id} onInvite={() => setShare(c)} onDelete={() => doDelete(c)} />
                </div>
                <Link to={`/os/tagma/${c.id}`} data-testid={`tagma-campaign-${c.id}`}
                  className="group block h-full bg-white border border-navy/10 rounded-lg p-6 hover:-translate-y-1 hover:border-navy/25 transition-all duration-500">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-medium tracking-tight">{c.name}</h3>
                      <div className="text-sm text-navy/50 mt-1">{c.client || "—"}</div>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-navy/30 group-hover:text-violet transition-colors mr-8" />
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS[c.status] || STATUS.active}`}>{c.status}</span>
                    <span className="text-xs text-navy/40">ROI {c.finance?.roi ?? 0}% · ROAS {c.roas ?? 0}x</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                    <div><div className="font-medium">{money(c.finance?.value)}</div><div className="overline text-navy/40 text-[0.6rem]">Valor</div></div>
                    <div><div className="font-medium">{money(c.finance?.expenses)}</div><div className="overline text-navy/40 text-[0.6rem]">Gastos</div></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="tagma-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">Nueva campaña</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required data-testid="tagma-name" placeholder="Nombre de campaña" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input data-testid="tagma-client" placeholder="Cliente" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input type="number" data-testid="tagma-budget" placeholder="Presupuesto" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <button data-testid="tagma-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">Crear campaña</button>
            </form>
          </motion.div>
        </div>
      )}

      {share && <ShareDialog collection="tagma_campaigns" id={share.id} title={share.name} onClose={() => setShare(null)} />}
      {trashOpen && <TrashDialog onClose={() => setTrashOpen(false)} onChanged={load} recoverPath="/tagma/recover" testid="tagma-trash-dialog" />}
    </OSLayout>
  );
}
